import React, { useState, useEffect } from "react";
import FacultyDashboardLayout from "../FacultyDashboardLayout";
import {
  getFacultyNotes,
  deleteNote,
  uploadNote,
  getFacultyDetails,
} from "../../../utils/Api";
import { toast } from "react-toastify";

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState("all");
  const [newNote, setNewNote] = useState({
    title: "",
    description: "",
    batchId: "",
    subjectId: "",
    file: null,
  });

  useEffect(() => {
    fetchNotes();
    fetchBatchesAndSubjects();
  }, []);

  const fetchNotes = async () => {
    try {
      const res = await getFacultyNotes();
      setNotes(res.notes);
    } catch (error) {
      toast.error("Failed to fetch notes");
    }
  };

  const fetchBatchesAndSubjects = async () => {
    try {
      const res = await getFacultyDetails();
      setBatches(res.batches);
      setSubjects(res.subjects);
    } catch (error) {
      toast.error("Failed to fetch batches or subjects");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNote(id);
      fetchNotes();
      toast.success("Note deleted successfully");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNote({ ...newNote, [name]: value });
  };

  const handleFileChange = (e) => {
    setNewNote({ ...newNote, file: e.target.files[0] });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    const selectedBatch = batches.find((b) => b._id === newNote.batchId);
    const selectedSubject = subjects.find((s) => s._id === newNote.subjectId);

    formData.append("title", newNote.title);
    formData.append("description", newNote.description);
    formData.append("batch[id]", newNote.batchId);
    formData.append("batch[name]", selectedBatch.batchName);
    formData.append("subject[id]", newNote.subjectId);
    formData.append("subject[name]", selectedSubject.subjectName);
    formData.append("file", newNote.file);

    try {
      await uploadNote(formData);
      fetchNotes();
      setShowModal(false);
      toast.success("Note uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload note");
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return "mdi-file-pdf-box";
      case "doc":
      case "docx":
        return "mdi-file-word-box";
      case "ppt":
      case "pptx":
        return "mdi-file-powerpoint-box";
      case "xls":
      case "xlsx":
        return "mdi-file-excel-box";
      default:
        return "mdi-file";
    }
  };

  // Group notes by subject
  const groupedNotes = notes.reduce((acc, note) => {
    const subjectName = note.subject.name;
    if (!acc[subjectName]) {
      acc[subjectName] = [];
    }
    acc[subjectName].push(note);
    return acc;
  }, {});

  const filteredNotes =
    selectedSubjectFilter === "all"
      ? groupedNotes
      : {
          [selectedSubjectFilter]: groupedNotes[selectedSubjectFilter] || [],
        };

  return (
    <FacultyDashboardLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">My Notes</h1>
          <div className="flex items-center space-x-4">
            <select
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm"
              value={selectedSubjectFilter}
              onChange={(e) => setSelectedSubjectFilter(e.target.value)}
            >
              <option value="all">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject._id} value={subject.subjectName}>
                  {subject.subjectName}
                </option>
              ))}
            </select>
            <button
              className="bg-blue-600 text-white px-6 py-2 rounded-lg shadow-md hover:bg-blue-700 transition-transform transform hover:scale-105"
              onClick={() => setShowModal(true)}
            >
              <i className="mdi mdi-upload mr-2"></i>
              Upload New Note
            </button>
          </div>
        </div>

        {Object.keys(filteredNotes).length > 0 ? (
          Object.keys(filteredNotes).map((subjectName) => (
            <div key={subjectName} className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-700 mb-4 border-b-2 border-gray-200 pb-2">
                {subjectName}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredNotes[subjectName].map((note) => (
                  <div
                    key={note._id}
                    className="bg-white rounded-lg shadow-lg p-5 flex flex-col justify-between transition-shadow hover:shadow-xl"
                  >
                    <div>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <i
                            className={`mdi ${getFileIcon(
                              note.fileName,
                            )} text-4xl text-blue-500 mr-4`}
                          ></i>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800">
                              {note.title}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {note.batch.name}
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-3">
                        {note.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-5 pt-4 border-t">
                      <div className="text-xs text-gray-400">
                        {new Date(note.createdAt).toLocaleDateString()}
                      </div>
                      <div className="flex space-x-2">
                        <a
                          href={note.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                          title="View Note"
                        >
                          <i className="mdi mdi-eye text-xl"></i>
                        </a>
                        <button
                          className="text-gray-500 hover:text-gray-700 cursor-not-allowed"
                          title="Edit Note (Coming Soon)"
                          disabled
                        >
                          <i className="mdi mdi-pencil text-xl"></i>
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          title="Delete Note"
                          onClick={() => handleDelete(note._id)}
                        >
                          <i className="mdi mdi-delete text-xl"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-16">
            <i className="mdi mdi-note-off-outline text-6xl text-gray-300"></i>
            <p className="text-gray-500 mt-4">
              You haven't uploaded any notes yet.
            </p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-6">Upload New Note</h2>
            <form onSubmit={handleUpload}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  name="title"
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  name="description"
                  onChange={handleInputChange}
                ></textarea>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch
                  </label>
                  <select
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    name="batchId"
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Batch</option>
                    {batches.map((batch) => (
                      <option key={batch._id} value={batch._id}>
                        {batch.batchName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <select
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    name="subjectId"
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((subject) => (
                      <option key={subject._id} value={subject._id}>
                        {subject.subjectName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File
                </label>
                <input
                  type="file"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={handleFileChange}
                  required
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                >
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </FacultyDashboardLayout>
  );
};

export default Notes;
